import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { runCheckout } from "../lib/payments";
import { useAuth } from "../context/AuthContext";
import { Loader2, Send, Gift, CheckCircle2, ArrowRight } from "lucide-react";

const RELATIONSHIPS = [
  "friend", "partner", "spouse", "colleague", "teammate", "manager",
  "sibling", "cousin", "parent", "child", "family member",
];

export default function InviteSend() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ invitee_email: "", invitee_name: "", relationship: "friend" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/start"); return; }
    api.me().then(setMe).catch(() => {});
  }, [user, navigate]);

  if (!me) return <div className="max-w-lg mx-auto px-5 py-24 text-center text-[#918780]">Loading…</div>;

  const latest = me.assessments[0];
  const freeAvailable = !me.user.free_invite_used;
  const credits = me.user.invite_credits;
  const needsPayment = !freeAvailable && credits <= 0;

  const doSend = async () => {
    if (!form.invitee_email) { toast.error("Please add their email"); return; }
    if (!latest) { toast.error("Take your assessment first"); navigate("/assessment"); return; }
    setSending(true);
    try {
      await api.createInvite({ assessment_id: latest.id, ...form });
      setDone(true);
      toast.success("Invitation sent!");
    } catch (err) {
      if (err?.response?.status === 402) { toast.error("Please add an invite first"); }
      else toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const payExtra = async () => {
    setSending(true);
    try {
      const { order } = await api.checkout("extra_invite");
      const payload = await runCheckout(order, user);
      const { user: updated } = await api.confirm(order.order_id, payload);
      login({ ...updated, token: user.token });
      const fresh = await api.me(); setMe(fresh);
      toast.success("Invite added — you can send now");
    } catch (err) {
      if (err?.message === "cancelled") toast.info("Payment cancelled");
      else toast.error(err?.response?.data?.detail || "Payment failed");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl border border-[#E8E1D9] bg-white focus:outline-none focus:border-[#6B21A8] focus:ring-2 focus:ring-[#6B21A8]/15 transition";

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-5 py-20 text-center fade-up">
        <span className="w-16 h-16 rounded-2xl bg-[#F2E9FA] text-[#3F3A44] grid place-items-center mx-auto mb-5">
          <CheckCircle2 size={30} />
        </span>
        <h1 className="font-display text-3xl font-bold text-[#2B2625] mb-3">Invitation on its way ✨</h1>
        <p className="text-[#6E655F] mb-8">
          We've emailed <b>{form.invitee_email}</b> a warm invite. The moment they finish, you'll both
          receive your shared compatibility report.
        </p>
        <Link to="/dashboard" data-testid="invite-done-dashboard"
          className="inline-flex items-center gap-2 bg-[#6B21A8] hover:bg-[#4C1D95] text-white font-semibold py-3 px-7 rounded-xl transition-colors">
          Back to dashboard <ArrowRight size={18} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-5 py-14 fade-up">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-display text-3xl font-bold text-[#2B2625] mb-2">Invite someone you're curious about</h1>
        <p className="text-[#6E655F] mb-6">
          Pick one person and discover how you two truly connect — the ways you complement each other,
          and how to grow closer in life and at work.
        </p>
      </motion.div>

      <div className={`rounded-xl px-4 py-3 mb-6 text-sm font-medium flex items-center gap-2 ${
        needsPayment ? "bg-[#FEF6E8] text-[#3B0764]" : "bg-[#F2E9FA] text-[#3F3A44]"}`}>
        <Gift size={16} />
        {freeAvailable ? "Your first invite is included — free" :
          credits > 0 ? `You have ${credits} invite${credits > 1 ? "s" : ""} ready to send` :
          `Add another invite for ${me.pricing.extra_invite_display}`}
      </div>

      <div className="bg-white border border-[#E8E1D9] rounded-2xl p-7 shadow-sm space-y-4">
        <div>
          <label className="text-sm font-semibold text-[#2B2625] block mb-1.5">Their name (optional)</label>
          <input data-testid="invite-name" className={field} value={form.invitee_name}
                 onChange={(e) => setForm({ ...form, invitee_name: e.target.value })} placeholder="e.g. Arjun" />
        </div>
        <div>
          <label className="text-sm font-semibold text-[#2B2625] block mb-1.5">Their email</label>
          <input data-testid="invite-email" type="email" className={field} value={form.invitee_email}
                 onChange={(e) => setForm({ ...form, invitee_email: e.target.value })} placeholder="them@example.com" />
        </div>
        <div>
          <label className="text-sm font-semibold text-[#2B2625] block mb-1.5">How do you know each other?</label>
          <select data-testid="invite-relationship" className={field} value={form.relationship}
                  onChange={(e) => setForm({ ...form, relationship: e.target.value })}>
            {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
          </select>
        </div>

        {needsPayment ? (
          <button data-testid="invite-pay" onClick={payExtra} disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#7C3AED] hover:bg-[#5B21B6] text-[#2B2625] font-semibold py-3.5 rounded-xl transition-colors active:scale-[0.98] disabled:opacity-60">
            {sending ? <Loader2 className="animate-spin" size={18} /> : `Add invite for ${me.pricing.extra_invite_display}`}
          </button>
        ) : (
          <button data-testid="invite-send" onClick={doSend} disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#6B21A8] hover:bg-[#4C1D95] text-white font-semibold py-3.5 rounded-xl transition-colors active:scale-[0.98] disabled:opacity-60">
            {sending ? <Loader2 className="animate-spin" size={18} /> : <><Send size={17} /> Send invitation</>}
          </button>
        )}
      </div>
    </div>
  );
}
